/**
 * Product Showcase by Notch
 * Theme Editor block focus + link-safe desktop drag.
 * Keeps scroll-snap-slider for arrows/slide math, but does not enable the
 * shared ScrollSnapDraggable plugin (it preventDefaults mousedown/mouseup and
 * breaks link activation). Touch uses native overflow scrolling.
 */
(function () {
  const SECTION_SELECTOR = '.product-showcase-notch';
  const SLIDE_SELECTOR = '.product-showcase-notch__slide';
  const TRACK_SELECTOR = '.product-showcase-notch__track[is="scroll-snap-slider"]';
  const DRAG_THRESHOLD = 8;

  function closestSection(node) {
    return node && node.closest ? node.closest(SECTION_SELECTOR) : null;
  }

  function isPsnDraggable(track) {
    return (track?.dataset?.psnDraggable || 'false') === 'true';
  }

  function getNearestChildIndex(track) {
    const children = Array.prototype.filter.call(track.children, (el) => el.matches(SLIDE_SELECTOR));
    let best = 0;
    let bestDist = Infinity;
    const scrollLeft = track.scrollLeft;

    children.forEach((slide, index) => {
      const dist = Math.abs(slide.offsetLeft - scrollLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });

    return best;
  }

  function clearDragStyles(track) {
    track.classList.remove('-dragging');
    track.style.scrollBehavior = '';
    track.style.scrollSnapStop = '';
    track.style.scrollSnapType = '';
  }

  function snapAfterDrag(track) {
    clearDragStyles(track);
    if (typeof track.slideTo !== 'function') return;
    try {
      track.slideTo(getNearestChildIndex(track));
    } catch (e) {
      /* ignore before slider init */
    }
  }

  function bindLinkSafeDrag(section) {
    const track = section.querySelector(TRACK_SELECTOR);
    if (!track || track.dataset.psnDragBound === 'true') return;
    track.dataset.psnDragBound = 'true';

    if (!isPsnDraggable(track)) return;

    let pointerId = null;
    let startX = 0;
    let lastX = 0;
    let active = false;
    let isDragging = false;
    let suppressClick = false;

    function endDrag() {
      if (!active) return;

      const didDrag = isDragging;
      const capturedId = pointerId;

      active = false;
      isDragging = false;
      pointerId = null;

      if (capturedId !== null && track.hasPointerCapture?.(capturedId)) {
        try {
          track.releasePointerCapture(capturedId);
        } catch (e) {
          /* ignore */
        }
      }

      if (didDrag) {
        suppressClick = true;
        snapAfterDrag(track);
      }
    }

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      // Preserve modifier / non-primary open-in-new-tab style clicks
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      active = true;
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      isDragging = false;
      suppressClick = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (!active || event.pointerId !== pointerId) return;
      if (event.pointerType !== 'mouse') return;

      const dx = event.clientX - startX;

      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;

        isDragging = true;
        track.classList.add('-dragging');
        track.style.scrollBehavior = 'auto';
        track.style.scrollSnapStop = 'unset';
        track.style.scrollSnapType = 'none';

        try {
          track.setPointerCapture(pointerId);
        } catch (e) {
          /* ignore */
        }
      }

      event.preventDefault();
      const delta = lastX - event.clientX;
      lastX = event.clientX;
      track.scrollLeft += delta;
    });

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener('lostpointercapture', () => {
      if (!active) return;
      const didDrag = isDragging;
      active = false;
      isDragging = false;
      pointerId = null;
      if (didDrag) {
        suppressClick = true;
        snapAfterDrag(track);
      }
    });

    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      },
      true
    );

    track.addEventListener('dragstart', (event) => {
      if (active || isDragging) event.preventDefault();
    });
  }

  function initSection(section) {
    if (!section) return;
    bindLinkSafeDrag(section);
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  function onBlockSelect(event) {
    const slide = event.target.closest(SLIDE_SELECTOR);
    if (!slide) return;
    const section = closestSection(slide);
    if (!section) return;

    const track = section.querySelector(TRACK_SELECTOR);
    if (!track || typeof track.slideTo !== 'function') return;

    const index = Number(slide.dataset.index);
    if (!Number.isNaN(index)) {
      requestAnimationFrame(() => {
        try {
          track.slideTo(index);
        } catch (e) {
          /* ignore */
        }
      });
    }
  }

  function onSectionLoad(event) {
    const section = event.target.querySelector
      ? event.target.querySelector(SECTION_SELECTOR) || (event.target.matches?.(SECTION_SELECTOR) ? event.target : null)
      : null;
    initSection(section);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:block:select', onBlockSelect);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
